import React from 'react';
import { useNavigate } from 'react-router-dom';
import pb, { customApi } from '../../pocketbase';
import { applyGender, getCachedGender } from '../../utils/genderUtils';
import { getUserNoteForCard } from '../../api/notes';
import './DailyQuestionBanner.css';
import BackgroundSlideshow from '../BackgroundSlideshow/BackgroundSlideshow';

const CACHE_KEY_PREFIX = 'daily_question_';
const GAME_ID_CACHE_KEY = 'daily_game_id';



// Получаем "эффективную" дату (с учётом того, что до 3 утра считается предыдущий день)
const getEffectiveDate = (date = new Date()) => {
    const effectiveDate = new Date(date);
    if (effectiveDate.getHours() < 3) {
        effectiveDate.setDate(effectiveDate.getDate() - 1);
    }
    return effectiveDate;
};

// Получаем начало дня для сравнения дат
const getStartOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const DailyQuestionBanner = React.forwardRef(function DailyQuestionBanner({
    onOpenNote,
    onQuestionOpened,
    selectedDate,
    onDateChange
}, ref) {
    const navigate = useNavigate();
    const [timeLeft, setTimeLeft] = React.useState('...');
    const [answeredCount, setAnsweredCount] = React.useState(0);
    const [totalCount] = React.useState(14);
    const [isOpened, setIsOpened] = React.useState(false);
    const [hasAnswered, setHasAnswered] = React.useState(false);
    const [isAvailable, setIsAvailable] = React.useState(false);
    const [questionText, setQuestionText] = React.useState('');
    const [displayedText, setDisplayedText] = React.useState('');
    const [isTyping, setIsTyping] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [gameId, setGameId] = React.useState(null);
    const [currentCard, setCurrentCard] = React.useState(null);

    // Состояние для свайп-анимации
    const [swipeDirection, setSwipeDirection] = React.useState(null);
    const [isAnimating, setIsAnimating] = React.useState(false);

    // Для обработки свайпов
    const touchStartX = React.useRef(null);
    const touchEndX = React.useRef(null);
    const minSwipeDistance = 50;

    // Определяем текущую дату для отображения
    const currentViewDate = React.useMemo(() => {
        if (selectedDate) {
            return new Date(selectedDate);
        }
        return getEffectiveDate();
    }, [selectedDate]);

    // Проверяем, является ли выбранная дата сегодняшней
    const isToday = React.useMemo(() => {
        const today = getEffectiveDate();
        const viewDate = getStartOfDay(currentViewDate);
        const todayStart = getStartOfDay(today);
        return viewDate.getTime() === todayStart.getTime();
    }, [currentViewDate]);

    // Проверяем, можно ли перейти вперёд (не в будущее)
    const canGoForward = React.useMemo(() => {
        const today = getStartOfDay(getEffectiveDate());
        const viewDate = getStartOfDay(currentViewDate);
        return viewDate < today;
    }, [currentViewDate]);

    // Expose methods via ref for parent components
    React.useImperativeHandle(ref, () => ({
        markAsAnswered: () => setHasAnswered(true),
        goToDate: (date) => {
            if (onDateChange) {
                onDateChange(date);
            }
        }
    }), [onDateChange]);

    // Вызываем колбэк при открытии вопроса
    React.useEffect(() => {
        if (isOpened && onQuestionOpened) {
            onQuestionOpened();
        }
    }, [isOpened, onQuestionOpened]);

    // Получаем game_id для 'daily-questions' (один раз)
    React.useEffect(() => {
        const fetchGameId = async () => {
            const cached = localStorage.getItem(GAME_ID_CACHE_KEY);
            if (cached) {
                setGameId(cached);
                return;
            }

            try {
                // Use custom API endpoint to get game by slug (bypasses List rules)
                const response = await customApi('GET', '/api/custom/game-by-slug/daily-questions');
                if (response && response.id) {
                    setGameId(response.id);
                    localStorage.setItem(GAME_ID_CACHE_KEY, response.id);
                }
            } catch (error) {
                console.error('Error fetching game id:', error);
            }
        };

        fetchGameId();
    }, []);

    // Функция подсчета прогресса за текущий месяц
    const fetchAnsweredCount = React.useCallback(async () => {
        try {
            const currentMonth = new Date().toISOString().slice(0, 7) + '-01';
            const records = await pb.collection('daily_openings').getList(1, 1, {
                filter: `opened_at >= '${currentMonth}'`,
            });
            setAnsweredCount(records.totalItems);
        } catch (err) {
            console.error('Error fetching answered count:', err);
        }
    }, []);

    // Загрузка карточки для конкретной даты
    const fetchCardForDate = React.useCallback(async (targetDate) => {
        if (!gameId) return;

        const cardDay = targetDate.getDate();
        const dateStr = targetDate.toISOString().slice(0, 10);
        const cacheKey = CACHE_KEY_PREFIX + dateStr + '_day' + cardDay;
        const startOfMonth = targetDate.toISOString().slice(0, 7) + '-01';

        let cardToUse = null;

        // 1. Проверяем кэш
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
            const parsed = JSON.parse(cachedData);
            cardToUse = parsed.card;

            if (parsed.isOpened) {
                setCurrentCard(parsed.card);
                setIsOpened(true);
                if (parsed.questionText) {
                    const userGender = getCachedGender();
                    const processedText = applyGender(parsed.questionText, userGender);
                    setQuestionText(processedText);
                    setDisplayedText(processedText);
                }

                try {
                    const existingNote = await getUserNoteForCard(parsed.card.id);
                    setHasAnswered(!!existingNote);
                } catch (err) {
                    console.error('Error checking for existing note:', err);
                    setHasAnswered(false);
                }

                return;
            }
        }

        // 2. Если карты нет в кэше, грузим данные
        if (!cardToUse) {
            try {
                console.log('DEBUG card fetch:', { gameId, cardDay, filter: `game = '${gameId}' && idx = ${cardDay}` });
                const card = await pb.collection('cards').getFirstListItem(
                    `game = '${gameId}' && idx = ${cardDay}`
                );
                if (card) {
                    cardToUse = card;
                }
            } catch (error) {
                console.error('Error fetching card:', error);
            }
        }

        if (cardToUse) {
            setCurrentCard(cardToUse);

            // 3. Проверяем статус открытия в БД
            let opening = null;
            try {
                const records = await pb.collection('daily_openings').getList(1, 1, {
                    filter: `card_idx = ${cardDay} && opened_at >= '${startOfMonth}'`,
                });
                if (records.items.length > 0) {
                    opening = records.items[0];
                }
            } catch (error) {
                console.log('No opening found');
            }

            if (opening) {
                let fullQuestion = cardToUse.question + (cardToUse.subquestion ? '\n\n' + cardToUse.subquestion : '');
                const userGender = getCachedGender();
                fullQuestion = applyGender(fullQuestion, userGender);

                setIsOpened(true);
                setQuestionText(fullQuestion);
                setDisplayedText(fullQuestion);

                try {
                    const existingNote = await getUserNoteForCard(cardToUse.id);
                    setHasAnswered(!!existingNote);
                } catch (err) {
                    console.error('Error checking for existing note:', err);
                    setHasAnswered(false);
                }

                localStorage.setItem(cacheKey, JSON.stringify({
                    card: cardToUse,
                    isOpened: true,
                    questionText: fullQuestion
                }));
            } else {
                setIsOpened(false);
                setQuestionText('');
                setDisplayedText('');
                setHasAnswered(false);
                if (!cachedData) {
                    localStorage.setItem(cacheKey, JSON.stringify({
                        card: cardToUse,
                        isOpened: false,
                        questionText: ''
                    }));
                }
            }
        }
    }, [gameId]);

    // Загружаем карточку при смене даты или gameId
    React.useEffect(() => {
        fetchCardForDate(currentViewDate);
        fetchAnsweredCount();
    }, [currentViewDate, fetchCardForDate, fetchAnsweredCount]);

    // Эффект печатной машинки
    React.useEffect(() => {
        if (!isTyping || !questionText) return;

        let currentIndex = 0;
        setDisplayedText('');

        const typingInterval = setInterval(() => {
            if (currentIndex < questionText.length) {
                setDisplayedText(questionText.slice(0, currentIndex + 1));
                currentIndex++;
            } else {
                setIsTyping(false);
                clearInterval(typingInterval);
            }
        }, 30);

        return () => clearInterval(typingInterval);
    }, [isTyping, questionText]);

    // Таймер только для текущего дня
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (!isToday) {
            setTimeLeft('');
            setIsAvailable(true); // Для прошлых дней всегда "доступно" в смысле просмотра
            return;
        }

        const updateTimer = () => {
            const now = new Date();
            const hours = now.getHours();

            // Вопрос всегда доступен (нет промежутка недоступности)
            setIsAvailable(true);

            // Таймер всегда показывает время до 3:00
            const targetTime = new Date(now);
            if (hours >= 3) {
                // После 3 утра -> следующий день в 3:00
                targetTime.setDate(targetTime.getDate() + 1);
            }
            targetTime.setHours(3, 0, 0, 0);

            const msLeft = Math.max(0, targetTime - now);

            const hoursLeft = Math.floor(msLeft / (1000 * 60 * 60));
            const minutes = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((msLeft % (1000 * 60)) / 1000);

            setTimeLeft(`${hoursLeft}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);

            if (msLeft <= 1000) {
                setTimeout(() => {
                    fetchCardForDate(currentViewDate);
                    fetchAnsweredCount();
                }, 1500);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
    }, [isOpened, isToday, currentViewDate, fetchCardForDate, fetchAnsweredCount]);

    // Навигация по дням
    const navigateDay = React.useCallback((direction) => {
        if (isAnimating) return;

        // Проверяем, можем ли перейти вперёд
        if (direction === 1 && !canGoForward) return;

        // direction 1 (вперед) -> уходим влево ('left')
        // direction -1 (назад) -> уходим вправо ('right')
        setSwipeDirection(direction === 1 ? 'left' : 'right');
        setIsAnimating(true);

        setTimeout(() => {
            const newDate = new Date(currentViewDate);
            newDate.setDate(newDate.getDate() + direction);

            if (onDateChange) {
                onDateChange(newDate);
            }

            // direction 1 (вперед) -> приходим справа ('enter-right')
            // direction -1 (назад) -> приходим слева ('enter-left')
            setSwipeDirection(direction === 1 ? 'enter-right' : 'enter-left');

            setTimeout(() => {
                setSwipeDirection(null);
                setIsAnimating(false);
            }, 200);
        }, 150);
    }, [isAnimating, canGoForward, currentViewDate, onDateChange]);

    // Обработчики свайпов
    const handleTouchStart = (e) => {
        if (isAnimating) return;
        touchStartX.current = e.touches[0].clientX;
        touchEndX.current = null;
    };

    const handleTouchMove = (e) => {
        if (isAnimating) return;
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (isAnimating) return;
        if (!touchStartX.current || !touchEndX.current) return;

        const distance = touchStartX.current - touchEndX.current;
        const isSwipe = Math.abs(distance) > minSwipeDistance;

        if (isSwipe) {
            if (distance > 0) {
                // Свайп влево - следующий день
                navigateDay(1);
            } else {
                // Свайп вправо - предыдущий день
                navigateDay(-1);
            }
        }

        touchStartX.current = null;
        touchEndX.current = null;
    };

    // Обработчик открытия вопроса / заметки / просмотра ответа
    const handleOpenQuestion = async () => {
        if (!currentCard || loading) return;

        // Если не сегодняшний день
        if (!isToday) {
            if (isOpened) {
                if (hasAnswered) {
                    navigate('/my-notes', {
                        state: { openNoteCardId: currentCard.id }
                    });
                } else if (onOpenNote) {
                    onOpenNote(currentCard, gameId);
                }
            }
            return;
        }

        // Если вопрос уже открыт
        if (isOpened) {
            if (hasAnswered) {
                navigate('/my-notes', {
                    state: { openNoteCardId: currentCard.id }
                });
                return;
            }
            if (onOpenNote) {
                onOpenNote(currentCard, gameId);
            }
            return;
        }

        setLoading(true);
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
            const offsetMin = -new Date().getTimezoneOffset();

            const data = await customApi('POST', '/api/custom/daily-open', {
                card_idx: currentCard.idx,
                card_id: currentCard.id
            });

            if (data.error) {
                const msg = String(data.error || '');
                if (msg.includes('rate_limit')) {
                    alert('Следующий вопрос будет доступен позже');
                } else if (msg.includes('duplicate') || msg.includes('already')) {
                    alert('Этот вопрос уже открыт сегодня');
                } else {
                    alert('Ошибка при открытии вопроса: ' + msg);
                }
                setLoading(false);
                return;
            }

            const now = new Date();
            const hours = now.getHours();
            const cardDay = hours < 6
                ? new Date(now.setDate(now.getDate() - 1)).getDate()
                : new Date().getDate();
            const today = new Date().toISOString().slice(0, 10);
            const cacheKey = CACHE_KEY_PREFIX + today + '_day' + cardDay;

            let fullQuestion = currentCard.question + (currentCard.subquestion ? '\n\n' + currentCard.subquestion : '');

            const userGender = getCachedGender();
            fullQuestion = applyGender(fullQuestion, userGender);

            setIsOpened(true);
            setQuestionText(fullQuestion);
            setIsTyping(true);

            localStorage.setItem(cacheKey, JSON.stringify({
                card: currentCard,
                isOpened: true,
                questionText: fullQuestion
            }));

            await fetchAnsweredCount();

        } catch (err) {
            console.error('Error opening question:', err);
            alert('Произошла ошибка. Попробуйте еще раз.');
        } finally {
            setLoading(false);
        }
    };

    // Получаем класс анимации
    const getAnimationClass = () => {
        if (!swipeDirection) return '';
        switch (swipeDirection) {
            case 'left': return 'swipe-exit-left';
            case 'right': return 'swipe-exit-right';
            case 'enter-left': return 'swipe-enter-left';
            case 'enter-right': return 'swipe-enter-right';
            default: return '';
        }
    };

    // Определяем текст заголовка
    const getLabelText = () => {
        return 'Вопрос дня';
    };

    // Определяем текст в правой части хедера
    const getTimerText = () => {
        if (isToday) {
            return (
                <>
                    <img src="/icons/timer.svg" alt="" className="timer-icon" />
                    <span>{timeLeft}</span>
                </>
            );
        }
        return null;
    };

    // Определяем текст вопроса
    const getTitleText = () => {
        if (isOpened) {
            return displayedText;
        }
        if (isToday) {
            return <>Открой 14 вопросов<br />и получи игру в подарок</>;
        }
        return 'Вы сможете открыть этот вопрос в будущем';
    };

    // Определяем текст и состояние кнопки
    const getButtonState = () => {
        if (loading) {
            return { text: 'Загрузка...', disabled: true };
        }

        if (!currentCard) {
            return { text: 'Загрузка...', disabled: true };
        }

        if (isOpened) {
            return {
                text: hasAnswered ? 'Посмотреть ответ' : 'Ответить',
                disabled: false
            };
        }

        if (!isToday) {
            return { text: 'Недоступно', disabled: true };
        }

        if (!isAvailable) {
            return { text: 'Завтра утром', disabled: true };
        }

        return { text: 'Открыть вопрос', disabled: false };
    };

    const buttonState = getButtonState();
    const isPastUnopened = !isToday && !isOpened;

    return (
        <div
            className={`daily-question-banner ${getAnimationClass()} ${isPastUnopened ? 'past-unopened' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <BackgroundSlideshow className="daily-question-video" />

            <div className="daily-question-content">
                <div className="daily-question-header">
                    <span className="daily-question-label">
                        {getLabelText()}
                    </span>
                    <div className="daily-question-timer">
                        {getTimerText()}
                    </div>
                </div>

                <div className="daily-question-title">
                    {getTitleText()}
                </div>

                <div className="daily-question-actions">
                    <button
                        className="daily-question-button primary"
                        onClick={handleOpenQuestion}
                        disabled={buttonState.disabled}
                    >
                        {buttonState.text}
                    </button>
                    <button className="daily-question-button counter">
                        <img src="/icons/gift.svg" alt="" className="gift-icon" />
                        {answeredCount} / {totalCount}
                    </button>
                </div>
            </div>
        </div>
    );
});

export default DailyQuestionBanner;
